import React from "react";
import { Container } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { getYearGroupLabel } from "../../Constant/ConstantData";

export function convertTime(minutes) {
  const hours = Math.floor(minutes / 60); // Get the hours
  const remainingMinutes = Math.round(minutes % 60); // Round to avoid floating-point precision issues

  if (hours > 0 && remainingMinutes > 0) {
    return `${hours}h ${remainingMinutes}m`; // "Xh Ym"
  } else if (hours > 0) {
    return `${hours}h`; // Only hours
  } else {
    return `${remainingMinutes}m`; // Only minutes
  }
}

const CustomXAxisTick = ({ x, y, payload }) => {
  const value = payload?.value || "";

  // Split longer labels like "College (1st Year)" or "College (2nd Year)" into 2 lines
  let line1 = value;
  let line2 = "";

  if (value.includes("College")) {
    if (value.includes("(")) {
      const idx = value.indexOf("(");
      line1 = value.substring(0, idx).trim();
      line2 = value.substring(idx).trim();
    } else if (value.includes("1st")) {
      line1 = "College";
      line2 = "(1st Year)";
    } else if (value.includes("2nd")) {
      line1 = "College";
      line2 = "(2nd Year)";
    } else {
      line1 = "College";
    }
  } else if (value.length > 9 && value.includes(" ")) {
    const spaceIdx = value.lastIndexOf(" ");
    line1 = value.substring(0, spaceIdx);
    line2 = value.substring(spaceIdx + 1);
  }

  return (
    <g transform={`translate(${x},${y})`}>
      {line2 ? (
        <text
          textAnchor="middle"
          fill="#374151"
          fontSize={11}
          fontWeight="500"
          fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        >
          <tspan x="0" dy="10">
            {line1}
          </tspan>
          <tspan x="0" dy="13">
            {line2}
          </tspan>
        </text>
      ) : (
        <text
          dy={12}
          textAnchor="middle"
          fill="#374151"
          fontSize={11.5}
          fontWeight="500"
          fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        >
          {line1}
        </text>
      )}
    </g>
  );
};

export default function ActivityChart({
  activityData,
  title = "Student Activity Overview",
}) {
  const chartData =
    Array.isArray(activityData) && activityData.length > 0
      ? activityData.map((ele) => ({
          year:
            getYearGroupLabel(ele.year) ||
            (ele.year ? `Year ${ele.year}` : ""),
          rawYear: ele.year,
          activeHours: ele.activeHours * 60, // Convert hours to minutes
        }))
      : [];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { year, activeHours } = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: "#ffffff",
            padding: "12px 16px",
            border: "none",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            backdropFilter: "blur(8px)",
            fontSize: "14px",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          <p
            style={{
              margin: "0 0 8px 0",
              fontWeight: "600",
              color: "#1a1a1a",
              fontSize: "13px",
            }}
          >
            {year}
          </p>
          <p
            style={{
              margin: "0",
              color: "#4318ff",
              fontWeight: "700",
              fontSize: "15px",
            }}
          >
            {convertTime(activeHours)}
          </p>
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <Container fluid>
        <div
          className="position-relative"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "317px",
          }}
        >
          <p className="data-not-found-cmn">
            {` We couldn’t find any activity for the selected date range. Try changing your filters and check again.`}
          </p>
        </div>
      </Container>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={310}>
      <BarChart
        data={chartData}
        margin={{ top: 10, right: 15, left: 15, bottom: 55 }}
        maxBarSize={38}
      >
        <defs>
          <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4318ff" stopOpacity={1} />
            <stop offset="100%" stopColor="#6366f1" stopOpacity={0.8} />
          </linearGradient>
        </defs>
        <CartesianGrid
          strokeDasharray="2 4"
          stroke="#e5e7eb"
          strokeOpacity={0.6}
          horizontal={true}
          vertical={false}
        />
        <XAxis
          dataKey="year"
          tick={<CustomXAxisTick />}
          axisLine={{ stroke: "#d1d5db", strokeWidth: 1 }}
          tickLine={{ stroke: "#d1d5db", strokeWidth: 1 }}
          height={45}
          interval={0}
        />
        <YAxis
          tickFormatter={(value) => convertTime(value)}
          tick={{
            fontSize: 12,
            fill: "#6b7280",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
          axisLine={{ stroke: "#d1d5db", strokeWidth: 1 }}
          tickLine={{ stroke: "#d1d5db", strokeWidth: 1 }}
          width={65}
        />
        <Tooltip
          content={<CustomTooltip />}
          cursor={{ fill: "rgba(67, 24, 255, 0.05)", radius: 8 }}
        />
        <Bar
          dataKey="activeHours"
          fill="url(#barGradient)"
          name="Active Hours"
          radius={[10, 10, 0, 0]}
          stroke="#4318ff"
          strokeWidth={0}
        />
        <text
          x="50%"
          y={302}
          textAnchor="middle"
          fontSize="12"
          fill="#6b7280"
          fontWeight="500"
          fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        >
          Year/Group
        </text>
      </BarChart>
    </ResponsiveContainer>
  );
}
